import {
  calculateWorkOrderCosts,
  type SnapOperationCostParams,
  type SnapWorkOrderCostResults,
} from '@calculations/cost';
import {
  QtyPerType,
  type RunRateType,
  runRateTypeSchema,
  type ScrapYieldType,
} from '@snap-types/models/enums';
import { Decimal, ZERO } from '@snap-types/types/decimal';

export type ConfiguratorCostsOpInput = {
  resourceId: string;
  sequenceNum: number;
  stepKey: string;
  operationType: 'internal' | 'external';
  run: number;
  runType: RunRateType;
  burdenPerOperation: number;
  burdenPerHrRun: number;
  burdenPerUnitRun: number;
  burdenPerHrSetup: number;
  runCostPerHr: number;
  runCostPerUnit: number;
  setupCostPerHr: number;
  setupHrs: number;
  serviceChargePerUnit: number;
  serviceBaseCharge: number;
  serviceMinCharge: number;
  serviceId: string;
  scrapYieldType: ScrapYieldType;
  scrapYieldPercent: number;
  fixedScrap: number;
};

export type ConfiguratorCostsInput = ConfiguratorCostsOpInput[];

export type ConfiguratorOperationCosts = SnapOperationCostParams[];

export type ConfiguratorCostsOutput = SnapWorkOrderCostResults;

type ConfiguratorToOpParams = {
  quantity: number;
  op: ConfiguratorCostsOpInput;
  matUnitCost?: number;
  matQtyPerPiece?: number;
  washerMatUnitCost?: number | null;
};

function configuratorOpToSnap({
  quantity,
  op,
  matUnitCost,
  matQtyPerPiece,
  washerMatUnitCost,
}: ConfiguratorToOpParams): SnapOperationCostParams {
  const runRateType = runRateTypeSchema.parse(op.runType);
  let pieceIdx = 0;

  /**
   * Create dummy requirements here to calculate material cost.
   * The only relevant fields for us here are unitsPerPiece, matUnitCost, washerQtyPer and washerMatUnitCost for now
   */
  const requirements: SnapOperationCostParams['snapRequirements'] = [];

  if (matUnitCost && quantity && matQtyPerPiece) {
    requirements.push({
      unitMaterialCost: new Decimal(matUnitCost),
      unitLaborCost: ZERO,
      unitBurdenCost: ZERO,
      unitServiceCost: ZERO,
      qtyPer: new Decimal(matQtyPerPiece),
      qtyPerType: QtyPerType.START,
      fixedQty: ZERO,
      fixedCost: ZERO,
      scrapPercent: ZERO,
      pieceNo: ++pieceIdx * 10,
    });
  }

  if (quantity && washerMatUnitCost) {
    requirements.push({
      unitMaterialCost: new Decimal(washerMatUnitCost),
      unitLaborCost: ZERO,
      unitBurdenCost: ZERO,
      unitServiceCost: ZERO,
      qtyPer: new Decimal('1'),
      qtyPerType: QtyPerType.START,
      fixedQty: ZERO,
      fixedCost: ZERO,
      scrapPercent: ZERO,
      pieceNo: ++pieceIdx * 10,
    });
  }

  return {
    resourceId: op.resourceId,
    run: new Decimal(op.run),
    runCostPerHr: new Decimal(op.runCostPerHr),
    runCostPerUnit: new Decimal(op.runCostPerUnit),
    runType: runRateType,
    sequenceNo: op.sequenceNum,
    snapRequirements: requirements,
    burPerHrRun: new Decimal(op.burdenPerHrRun),
    burPerHrSetup: new Decimal(op.burdenPerHrSetup),
    burPerOperation: ZERO,
    // @TODO: NT - Where should this come from?
    // It looks like this isn't used for PCS_PER_HR calculations
    loadSizeQty: new Decimal(1),
    scrapYieldType: op.scrapYieldType,
    scrapYieldPct: new Decimal(op.scrapYieldPercent),
    fixedScrapUnits: new Decimal(op.fixedScrap),
    serviceBaseCharge: new Decimal(op.serviceBaseCharge),
    serviceMinCharge: new Decimal(op.serviceMinCharge),
    serviceChargePerUnit: new Decimal(op.serviceChargePerUnit),
    serviceId: op.serviceId,
    setupHrs: new Decimal(op.setupHrs),
    setupCostPerHr: new Decimal(op.setupCostPerHr),
  };
}

function findFirstInternalOpByStepKey(
  ops: ConfiguratorCostsInput,
  stepKey: string,
): ConfiguratorCostsOpInput | undefined {
  return ops.reduce<ConfiguratorCostsOpInput | undefined>((firstOp, op) => {
    if (op.stepKey !== stepKey || op.operationType !== 'internal') {
      return firstOp;
    }
    if (!firstOp || op.sequenceNum < firstOp.sequenceNum) {
      return op;
    }
    return firstOp;
  }, undefined);
}

export function calculateConfiguratorCosts(
  quantity: number,
  ops: ConfiguratorCostsInput,
  matUnitCost: number,
  matQtyPerPiece: number,
  washerMatUnitCost: number | null,
): {
  snapOperations: SnapOperationCostParams[];
  workOrderCosts: SnapWorkOrderCostResults;
} {
  const firstHeadingOperation = findFirstInternalOpByStepKey(ops, 'heading');

  if (!firstHeadingOperation) {
    throw new Error('Required heading operation not found');
  }

  const firstRollingOperation = findFirstInternalOpByStepKey(
    ops,
    'rolling_threading',
  );

  const snapOperations = ops.map((op) => {
    const configuratorToOpParams: ConfiguratorToOpParams = {
      quantity,
      op,
    };
    if (op.sequenceNum === firstHeadingOperation.sequenceNum) {
      // Material costs (other than washer) go on the first heading operation
      configuratorToOpParams.matUnitCost = matUnitCost;
      configuratorToOpParams.matQtyPerPiece = matQtyPerPiece;
    }

    if (op.sequenceNum === 10 && !firstRollingOperation) {
      // If there are no rolling operations, washer material costs go on the first operation
      configuratorToOpParams.washerMatUnitCost = washerMatUnitCost;
    }

    if (
      firstRollingOperation &&
      op.sequenceNum === firstRollingOperation.sequenceNum
    ) {
      // Washer material costs go on the first rolling operation if there is one
      configuratorToOpParams.washerMatUnitCost = washerMatUnitCost;
    }

    return configuratorOpToSnap(configuratorToOpParams);
  });

  const workOrderCosts = calculateWorkOrderCosts({
    quantity: new Decimal(quantity),
    snapWorkOrder: {
      snapOperations,
      hasSubWorkOrders: false,
      hasNestedRequirementWorkOrders: false,
      enableUnsupportedCalculationsFlag: false,
    },
  });

  return {
    snapOperations,
    workOrderCosts,
  };
}
